import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../config/firebase";
import { ArrowLeft, CheckCircle, Send } from "lucide-react";
import { toast } from "react-toastify";

// Assets oficiais mantendo a simbiose visual
import logoOriginal from "../../assets/images/logo_frotabaase_transp.png";
import illustrationLight from "../../assets/images/illustration-light.png";

const ForgotPasswordPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const brandColor = "#00a99d";

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Formato de e-mail inválido.")
        .required("O e-mail é obrigatório"),
    }),
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        await sendPasswordResetEmail(auth, values.email);
        setIsSubmitted(true);
      } catch (error) {
        console.error("Erro ao enviar e-mail de redefinição:", error);
        let errorMessage = "Não foi possível enviar o e-mail. Tente novamente.";
        if (error.code === "auth/user-not-found") {
          errorMessage = "Nenhuma conta encontrada com este e-mail.";
        }
        setFieldError("email", errorMessage);
        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="flex min-h-screen w-full font-sans bg-white dark:bg-dark-bg overflow-hidden">
      
      {/* 🚀 Lado Esquerdo: Identidade Visual Imersiva (Espelho do Login) */}
      <div className="hidden lg:flex relative w-1/2 flex-col justify-between overflow-hidden">
        
        {/* Imagem de Fundo Preenchendo 100% */}
        <img 
          src={illustrationLight} 
          alt="Background Real Benefícios" 
          className="absolute inset-0 w-full h-full object-cover z-0"
        />

        {/* Logotipo Gigante no Topo Esquerdo */}
        <div className="relative z-10 p-12">
          <img 
            src={logoOriginal} 
            alt="Logo Real Benefícios" 
            className="h-16 md:h-20 w-auto object-contain drop-shadow-sm" 
          />
        </div>

        {/* Texto de Impacto no Rodapé */}
        <div className="relative z-10 p-12 text-center pb-20">
          <p className="text-white/80 mt-4 text-sm font-medium">@ 2025 - Real Benefícios</p>
        </div>
      </div>

      {/* Lado Direito: Ação de Recuperação ou Sucesso */}
      <div className="flex flex-1 justify-center items-center p-8 lg:p-24">
        <div className="w-full max-w-md animate-fade-in">
          
          {isSubmitted ? (
            /* --- TELA DE SUCESSO (Visualmente Refinada) --- */
            <div className="text-center">
              <div className="mb-8 flex justify-center">
                <div className="p-5 bg-teal-50 dark:bg-teal-900/20 rounded-full border border-teal-100 dark:border-teal-800">
                  <CheckCircle className="h-16 w-16" style={{ color: brandColor }} />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">
                Verifique seu e-mail
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-10 leading-relaxed text-lg">
                Enviamos as instruções para <span className="font-bold text-gray-800 dark:text-white">{formik.values.email}</span>. 
                Se não chegar em alguns minutos, cheque a caixa de spam.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full py-4 px-8 rounded-full text-white font-bold transition-all duration-300 hover:scale-105 shadow-xl shadow-[#00a99d]/20 active:scale-95"
                style={{ backgroundColor: brandColor }}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar para o Login
              </Link>
            </div>
          ) : (
            /* --- FORMULÁRIO DE RECUPERAÇÃO --- */
            <>
              <h1 className="text-4xl leading-tight font-light text-gray-800 dark:text-white mb-16">
                Esqueceu sua senha? <br />
                <span className="font-bold">Nós te ajudamos.</span>
              </h1>

              <div className="mb-10">
                <h2 className="text-2xl font-bold mb-1" style={{ color: brandColor }}>
                  Redefinir Senha
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Informe o seu e-mail de acesso para enviarmos o link.
                </p>
              </div>

              <form onSubmit={formik.handleSubmit} noValidate className="space-y-12">
                {/* Campo E-mail Minimalista com Estilo Síncrono ao Login */}
                <div className="relative">
                  <label className="text-xs text-gray-400 font-medium uppercase tracking-wider">Seu e-mail de acesso</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    {...formik.getFieldProps("email")}
                    className={`w-full py-2 bg-transparent border-b-2 text-gray-800 dark:text-white focus:outline-none transition-all duration-300 ${
                      formik.touched.email && formik.errors.email
                        ? "border-red-500"
                        : "border-gray-300 dark:border-dark-border focus:border-[#00a99d]"
                    }`}
                    placeholder="exemplo@email.com"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <p className="absolute text-[10px] text-red-500 mt-1 uppercase font-bold tracking-tighter">
                      {formik.errors.email}
                    </p>
                  )}
                </div>

                <div className="flex flex-col space-y-6 pt-6">
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="w-full py-4 rounded-full text-white font-bold transition-all duration-300
                    focus:outline-none disabled:opacity-50 hover:scale-105 shadow-xl shadow-[#00a99d]/20 active:scale-95 flex items-center justify-center"
                    style={{ backgroundColor: brandColor }} 
                  >
                    {formik.isSubmitting ? "Enviando..." : (
                      <>
                        <span>Enviar Link de Redefinição</span>
                        <Send className="w-4 h-4 ml-3" />
                      </>
                    )}
                  </button>

                  <Link
                    to="/login"
                    className="text-sm font-semibold hover:opacity-70 transition-opacity flex items-center justify-center"
                    style={{ color: brandColor }}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Lembrou a senha? Voltar para o Login
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;